import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Key, CheckCircle2, XCircle, Activity, Radio, Server, RefreshCw, AlertTriangle, Lock, Eye, EyeOff } from "lucide-react";
import { logger } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/sentinel`;

function timeAgo(isoString) {
  if (!isoString) return "never";
  const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function OpsControlPanel({ ipLog, onIpLogRefresh }) {
  const [streamStatus, setStreamStatus] = useState(null);
  const [streamToggling, setStreamToggling] = useState(false);
  const [serverEvents, setServerEvents] = useState([]);

  const fetchStreamStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stream/status`);
      setStreamStatus(res.data);
    } catch (err) {
      logger.debug("[OPS] Stream status:", err.message);
    }
  }, []);

  const fetchServerEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ops/server-events`);
      setServerEvents(res.data.events || []);
    } catch (err) {
      logger.debug("[OPS] Server events:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchStreamStatus();
    fetchServerEvents();
    const interval = setInterval(fetchStreamStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStreamStatus, fetchServerEvents]);

  const handleStreamToggle = async () => {
    if (streamToggling) return;
    setStreamToggling(true);
    try {
      if (streamStatus?.is_running) {
        await axios.post(`${API}/stream/stop`);
      } else {
        await axios.post(`${API}/stream/start`, { force: true });
      }
      // Small delay for the backend to update state
      setTimeout(fetchStreamStatus, 500);
    } catch (err) {
      logger.warn("[OPS] Stream toggle:", err.response?.data?.detail || err.message);
    } finally {
      setStreamToggling(false);
    }
  };

  const handleBlockIp = async (ip) => {
    try {
      await axios.post(`${API}/ops/block-ip`, { ip });
      onIpLogRefresh();
    } catch (err) {
      logger.warn("[OPS] Block IP failed:", err.message);
    }
  };

  const handleUnblockIp = async (ip) => {
    try {
      await axios.post(`${API}/ops/unblock-ip`, { ip });
      onIpLogRefresh();
    } catch (err) {
      logger.warn("[OPS] Unblock IP failed:", err.message);
    }
  };

  return (
    <div className="mt-4 rounded-lg border" style={{ backgroundColor: "#161b22", borderColor: "#30363d" }}>
      <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "#30363d" }}>
        <Key size={14} style={{ color: "#f59e0b" }} />
        <span className="font-mono text-[10px] tracking-[0.12em] uppercase font-semibold" style={{ color: "#f59e0b" }}>
          OPS CONTROL PANEL
        </span>
      </div>

      <div className="p-4 space-y-4">
        {/* Live Stream Toggle */}
        <StreamToggleSection
          streamStatus={streamStatus}
          streamToggling={streamToggling}
          onToggle={handleStreamToggle}
        />

        {/* Manual Pull */}
        <ManualPullSection />

        {/* IP Access Log */}
        <IpAccessLog
          ipLog={ipLog}
          onBlockIp={handleBlockIp}
          onUnblockIp={handleUnblockIp}
        />

        {/* Server Events Log */}
        <ServerEventsLog events={serverEvents} />

        {/* Password Management */}
        <PasswordChangeSection />
      </div>
    </div>
  );
}

function ManualPullSection() {
  const [state, setState] = useState("idle"); // idle | confirming | pulling | cooldown
  const [estimate, setEstimate] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  // Fetch estimate when confirming
  const handleClick = async () => {
    if (state === "idle") {
      try {
        const res = await axios.get(`${API}/pull/estimate`);
        setEstimate(res.data);
        setState("confirming");
      } catch {
        setState("idle");
      }
    }
  };

  const handleConfirm = async () => {
    setState("pulling");
    setResult(null);
    setProgress(null);
    try {
      const res = await axios.post(`${API}/pull`);
      setResult(res.data);
    } catch (err) {
      setResult({ status: "error", error: err.response?.data?.detail || err.message });
    }
    setState("cooldown");
    setCooldownLeft(60);
  };

  // Cooldown timer
  useEffect(() => {
    if (state !== "cooldown") return;
    if (cooldownLeft <= 0) { setState("idle"); return; }
    const t = setTimeout(() => setCooldownLeft((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [state, cooldownLeft]);

  // Listen for pull_progress via WebSocket
  useEffect(() => {
    const handler = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "pull_progress") setProgress(msg.data);
      } catch {}
    };
    // Attach to existing ws connections via broadcast events
    window.__pullProgressHandler = handler;
    return () => { delete window.__pullProgressHandler; };
  }, []);

  const btnDisabled = state === "pulling" || state === "cooldown";

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <RefreshCw size={13} style={{ color: "#3b82f6" }} />
        <span className="font-mono text-[10px] tracking-[0.08em] uppercase" style={{ color: "#8b949e" }}>
          Manual Pull
        </span>
        <span className="font-mono text-[9px]" style={{ color: "#484f58" }}>
          (catch-up only — webhook handles live delivery)
        </span>
      </div>

      {/* Confirmation dialog */}
      {state === "confirming" && estimate && (
        <div className="p-2.5 rounded-lg mb-2" style={{
          backgroundColor: "rgba(245, 158, 11, 0.06)",
          border: "1px solid rgba(245, 158, 11, 0.25)",
        }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={12} style={{ color: "#f59e0b" }} />
            <span className="font-mono text-xs" style={{ color: "#f59e0b" }}>
              This will use ~{estimate.credits.toLocaleString()} credits ({estimate.accounts} accounts x 300)
            </span>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="pull-confirm-btn"
              onClick={handleConfirm}
              className="px-3 py-1 rounded font-mono text-xs font-medium"
              style={{
                backgroundColor: "rgba(59, 130, 246, 0.15)",
                color: "#3b82f6",
                border: "1px solid rgba(59, 130, 246, 0.4)",
              }}
            >
              Pull Now
            </button>
            <button
              data-testid="pull-cancel-btn"
              onClick={() => setState("idle")}
              className="px-3 py-1 rounded font-mono text-xs"
              style={{ color: "#8b949e", border: "1px solid rgba(139,148,158,0.2)" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Main button */}
      {state !== "confirming" && (
        <button
          data-testid="ops-manual-pull-btn"
          onClick={handleClick}
          disabled={btnDisabled}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-xs font-medium transition-all mb-2"
          style={{
            backgroundColor: btnDisabled ? "rgba(59,130,246,0.04)" : "rgba(59, 130, 246, 0.15)",
            color: "#3b82f6",
            border: "1px solid rgba(59, 130, 246, 0.4)",
            opacity: btnDisabled ? 0.5 : 1,
            cursor: btnDisabled ? "not-allowed" : "pointer",
          }}
        >
          <RefreshCw size={11} className={state === "pulling" ? "cw-spin" : ""} />
          {state === "pulling" ? "PULLING..." : state === "cooldown" ? `COOLDOWN ${cooldownLeft}s` : "PULL NOW"}
        </button>
      )}

      {/* Progress during pull */}
      {state === "pulling" && progress && (
        <div className="font-mono text-[10px] mb-2" style={{ color: "#8b949e" }}>
          Checking @{progress.handle} ({progress.checked}/{progress.total_handles}) · {progress.new_found} new
        </div>
      )}

      {/* Results */}
      {result && result.status === "error" && (
        <div className="font-mono text-xs mb-1" style={{ color: "#ef4444" }}>Error: {result.error}</div>
      )}
      {result && result.status === "ok" && (
        <div className="space-y-0.5">
          <div className="font-mono text-[10px] mb-1" style={{ color: "#22c55e" }}>
            +{result.total_new} new · {result.total_pending} queued · {result.total_skipped} dupes · ~{result.credits_used?.toLocaleString()} credits
          </div>
          {result.accounts?.map((a, i) => (
            <div key={`pull-${a.handle}-${i}`} className="flex items-center gap-2 font-mono text-[10px]">
              {a.status === "ok" ? (
                <CheckCircle2 size={9} style={{ color: a.new > 0 ? "#22c55e" : "#484f58" }} />
              ) : (
                <XCircle size={9} style={{ color: "#ef4444" }} />
              )}
              <span style={{ color: "#8b949e" }}>@{a.handle}</span>
              {a.status === "ok" ? (
                <span style={{ color: a.new > 0 ? "#22c55e" : "#484f58" }}>
                  {a.found} found · {a.new} new{a.pending > 0 ? ` · ${a.pending} queued` : ""}
                </span>
              ) : (
                <span style={{ color: "#ef4444" }}>{a.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IpAccessLog({ ipLog, onBlockIp, onUnblockIp }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Activity size={13} style={{ color: "#8b5cf6" }} />
        <span className="font-mono text-[10px] tracking-[0.08em] uppercase" style={{ color: "#8b949e" }}>
          IP Access Log ({ipLog.length})
        </span>
      </div>
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {ipLog.length === 0 ? (
          <p className="font-mono text-xs" style={{ color: "#484f58" }}>No login attempts recorded.</p>
        ) : (
          ipLog.map((entry) => (
            <div
              key={entry.ip}
              className="flex items-center justify-between px-3 py-2 rounded"
              style={{
                backgroundColor: entry.is_blocked ? "rgba(239, 68, 68, 0.06)" : "#0d1117",
                border: `1px solid ${entry.is_blocked ? "rgba(239, 68, 68, 0.3)" : "#21262d"}`,
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="font-mono text-xs font-medium"
                  style={{ color: entry.is_blocked ? "#ef4444" : "#e6edf3" }}
                >
                  {entry.ip}
                </span>
                <span className="font-mono text-[10px]" style={{ color: "#484f58" }}>
                  {entry.login_count || 0} logins
                  {entry.failed_count ? ` \u00b7 ${entry.failed_count} failed` : ""}
                </span>
                {entry.last_login && (
                  <span className="font-mono text-[10px]" style={{ color: "#484f58" }}>
                    last: {timeAgo(entry.last_login)}
                  </span>
                )}
                {entry.is_blocked && (
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{
                    backgroundColor: "rgba(239, 68, 68, 0.15)",
                    color: "#ef4444",
                  }}>
                    BLOCKED
                  </span>
                )}
              </div>
              <button
                data-testid={`ip-toggle-${entry.ip}`}
                onClick={() => entry.is_blocked ? onUnblockIp(entry.ip) : onBlockIp(entry.ip)}
                className="px-2 py-1 rounded font-mono text-[10px] font-medium transition-colors"
                style={{
                  backgroundColor: entry.is_blocked
                    ? "rgba(34, 197, 94, 0.12)"
                    : "rgba(239, 68, 68, 0.12)",
                  color: entry.is_blocked ? "#22c55e" : "#ef4444",
                  border: `1px solid ${entry.is_blocked ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)"}`,
                }}
              >
                {entry.is_blocked ? "UNBLOCK" : "BLOCK"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function StreamToggleSection({ streamStatus, streamToggling, onToggle }) {
  const isRunning = streamStatus?.is_running;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Radio size={13} style={{ color: isRunning ? "#22c55e" : "#ef4444" }} />
        <span className="font-mono text-[10px] tracking-[0.08em] uppercase" style={{ color: "#8b949e" }}>
          Live Stream
        </span>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg" style={{
        backgroundColor: isRunning ? "rgba(34, 197, 94, 0.04)" : "rgba(239, 68, 68, 0.04)",
        border: `1px solid ${isRunning ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)"}`,
      }}>
        <div className="flex items-center gap-3">
          {/* Status indicator */}
          <div className="flex items-center gap-2">
            <span
              className={isRunning ? "cw-live-dot" : ""}
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: isRunning ? "#22c55e" : "#ef4444",
              }}
            />
            <span className="font-mono text-xs font-semibold" style={{
              color: isRunning ? "#22c55e" : "#ef4444",
            }}>
              {isRunning ? "STREAMING" : "STOPPED"}
            </span>
          </div>

          {/* Details */}
          {streamStatus && isRunning && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px]" style={{ color: "#484f58" }}>
                {streamStatus?.webhook?.active
                  ? <><span style={{ color: "#22c55e" }}>Webhook: active</span>{streamStatus.posts_per_hour ? ` | ${streamStatus.posts_per_hour}/hr` : ""}</>
                  : <><span style={{ color: "#f59e0b" }}>Webhook: waiting</span>{streamStatus.posts_per_hour ? ` | ${streamStatus.posts_per_hour}/hr` : ""}</>
                }
              </span>
            </div>
          )}
        </div>

        {/* Toggle button */}
        <button
          data-testid="stream-toggle-btn"
          onClick={onToggle}
          disabled={streamToggling}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md font-mono text-xs font-semibold tracking-wide transition-all"
          style={{
            backgroundColor: isRunning
              ? "rgba(239, 68, 68, 0.12)"
              : "rgba(34, 197, 94, 0.12)",
            color: isRunning ? "#ef4444" : "#22c55e",
            border: `1px solid ${isRunning ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`,
            opacity: streamToggling ? 0.5 : 1,
            cursor: streamToggling ? "not-allowed" : "pointer",
          }}
        >
          <Radio size={12} />
          {streamToggling ? "..." : isRunning ? "STOP" : "START"}
        </button>
      </div>
    </div>
  );
}


function ServerEventsLog({ events }) {
  if (!events || events.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Server size={13} style={{ color: "#6366f1" }} />
        <span className="font-mono text-[10px] tracking-[0.08em] uppercase" style={{ color: "#8b949e" }}>
          Server Events ({events.length})
        </span>
      </div>
      <div className="space-y-1 max-h-[250px] overflow-y-auto">
        {events.map((ev, i) => {
          const isStartup = ev.event === "startup";
          const ts = ev.timestamp ? new Date(ev.timestamp) : null;
          const timeStr = ts
            ? ts.toLocaleString("en-US", {
                timeZone: "America/New_York",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })
            : "—";

          return (
            <div
              key={`sev-${ev.timestamp}-${i}`}
              className="flex items-center gap-3 px-3 py-2 rounded"
              style={{
                backgroundColor: "#0d1117",
                border: "1px solid #21262d",
              }}
            >
              <span
                className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: isStartup ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                  color: isStartup ? "#22c55e" : "#ef4444",
                  border: `1px solid ${isStartup ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
                }}
              >
                {isStartup ? "BOOT" : "STOP"}
              </span>

              <span className="font-mono text-xs" style={{ color: "#e6edf3" }}>
                {timeStr} ET
              </span>

              {isStartup && ev.mode && (
                <span className="font-mono text-[10px]" style={{ color: "#8b949e" }}>
                  mode={ev.mode}
                </span>
              )}

              {!isStartup && ev.uptime_seconds != null && (
                <span className="font-mono text-[10px]" style={{ color: "#8b949e" }}>
                  uptime={formatUptime(ev.uptime_seconds)}
                </span>
              )}

              {ev.hostname && (
                <span className="font-mono text-[10px]" style={{ color: "#484f58" }}>
                  {ev.hostname.slice(0, 16)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PasswordChangeSection() {
  const [target, setTarget] = useState("main"); // "main" or "ops"
  const [currentOps, setCurrentOps] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [status, setStatus] = useState(null); // {type: "success"|"error", msg: "..."}
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!currentOps || !newPassword) {
      setStatus({ type: "error", msg: "Both fields required" });
      return;
    }
    if (newPassword.length < 4) {
      setStatus({ type: "error", msg: "Minimum 4 characters" });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/ops/change-password`, {
        target,
        current_ops_password: currentOps,
        new_password: newPassword,
      });
      setStatus({ type: "success", msg: res.data.message });
      setNewPassword("");
      setCurrentOps("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = err.response?.status;
      if (code === 401) {
        setStatus({ type: "error", msg: "Wrong OPS password" });
      } else if (code === 404) {
        setStatus({ type: "error", msg: "Endpoint not found — deploy latest build" });
      } else {
        setStatus({ type: "error", msg: detail || "Failed to update" });
      }
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Lock size={13} style={{ color: "#f59e0b" }} />
        <span className="font-mono text-[10px] tracking-[0.08em] uppercase" style={{ color: "#8b949e" }}>
          Change Password
        </span>
      </div>

      <div className="p-3 rounded-lg" style={{ backgroundColor: "#0d1117", border: "1px solid #21262d" }}>
        {/* Target selector */}
        <div className="flex gap-2 mb-3">
          {["main", "ops"].map((t) => (
            <button
              key={t}
              data-testid={`pw-target-${t}`}
              onClick={() => { setTarget(t); setStatus(null); }}
              className="px-3 py-1.5 rounded font-mono text-[10px] font-semibold uppercase tracking-wide transition-all"
              style={{
                backgroundColor: target === t ? "rgba(245, 158, 11, 0.12)" : "transparent",
                color: target === t ? "#f59e0b" : "#484f58",
                border: `1px solid ${target === t ? "rgba(245, 158, 11, 0.4)" : "#21262d"}`,
              }}
            >
              {t === "main" ? "App Password" : "OPS Password"}
            </button>
          ))}
        </div>

        {/* Current OPS password */}
        <div className="relative mb-2">
          <input
            data-testid="pw-current-ops"
            type={showCurrent ? "text" : "password"}
            value={currentOps}
            onChange={(e) => setCurrentOps(e.target.value)}
            placeholder="Current OPS password"
            className="w-full px-3 py-2 pr-9 rounded font-mono text-xs"
            style={{ backgroundColor: "#161b22", border: "1px solid #30363d", color: "#e6edf3", outline: "none" }}
          />
          <button
            type="button"
            onClick={() => setShowCurrent(!showCurrent)}
            className="absolute right-2 top-1/2 -translate-y-1/2"
            style={{ color: "#484f58" }}
          >
            {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>

        {/* New password */}
        <div className="relative mb-3">
          <input
            data-testid="pw-new"
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={`New ${target === "main" ? "app" : "OPS"} password`}
            className="w-full px-3 py-2 pr-9 rounded font-mono text-xs"
            style={{ backgroundColor: "#161b22", border: "1px solid #30363d", color: "#e6edf3", outline: "none" }}
          />
          <button
            type="button"
            onClick={() => setShowNew(!showNew)}
            className="absolute right-2 top-1/2 -translate-y-1/2"
            style={{ color: "#484f58" }}
          >
            {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="pw-submit-btn"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 rounded font-mono text-xs font-semibold tracking-wide transition-all"
            style={{
              backgroundColor: "rgba(245, 158, 11, 0.12)",
              color: "#f59e0b",
              border: "1px solid rgba(245, 158, 11, 0.4)",
              opacity: loading ? 0.5 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "UPDATING..." : "UPDATE PASSWORD"}
          </button>
          {status && (
            <span className="font-mono text-[10px] flex items-center gap-1" style={{
              color: status.type === "success" ? "#22c55e" : "#ef4444",
            }}>
              {status.type === "success" ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
              {status.msg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
